<template>
    <div class="enrollment-wizard">
        <div class="wizard-container" :class="{'wizard-mobile': mobile}">
            <!-- Left Sidebar with Steps -->
            <wizard-sidebar 
                :current-step="currentStep" 
                :steps="activeSteps" 
                :mobile="mobile" 
            />

            <!-- Right Content Area -->
            <div class="wizard-content">
                <div class="content-header">
                    <span class="step-counter">{{ currentStep }}/{{ activeSteps.length }}</span>
                    <h3 class="content-title">{{ currentStepConfig.title }}</h3>
                    <p class="content-subtitle">{{ currentStepConfig.subtitle }}</p>
                </div>

                <v-form class="wizard-form">
                    <!-- Basic Info Step (only if user has no DOB) -->
                    <wizard-step-basic-info 
                        v-show="currentStepKey === 'basic'"
                        :date-of-birth.sync="formData.dateOfBirth"
                        :max-date-of-birth="maxDateOfBirth"
                    />

                    <!-- Evaluation Step (dynamic — calendar slot picker) -->
                    <wizard-step-evaluation 
                        v-show="currentStepKey === 'evaluation'"
                        v-model="formData.evaluationBookedStart"
                        :whatsapp-number="whatsappNumber"
                    />

                    <!-- Trial Class Step -->
                    <wizard-step-trial-class 
                        v-show="currentStepKey === 'trial'"
                        :trial-date.sync="formData.trialDate"
                        :trial-time.sync="formData.trialTime"
                        :min-trial-date="minTrialDate"
                        :mobile="mobile"
                    />

                    <!-- Weekly Schedule Step -->
                    <wizard-step-schedule 
                        v-show="currentStepKey === 'schedule'"
                        :class-duration.sync="formData.classDuration"
                        :classes-per-week.sync="formData.classesPerWeek"
                        :preferred-days.sync="formData.preferredDays"
                        :start-date.sync="formData.startDate"
                        :time-entries.sync="formData.timeEntries"
                        :course-duration-items="courseDurationItems"
                        :classes-per-week-items="classesPerWeekItems"
                        :min-start-date="minStartDate"
                        :mobile="mobile"
                    />

                    <!-- Final Details Step -->
                    <wizard-step-final 
                        v-show="currentStepKey === 'final'"
                        :comment.sync="formData.comment"
                    />
                </v-form>

                <!-- Navigation Buttons -->
                <div class="wizard-actions">
                    <span v-if="currentStep > 1" class="back-link" @click="previousStep">Back</span>
                    <div v-else></div>
                    
                    <button v-if="currentStep < activeSteps.length" class="primary-btn" @click="nextStep">
                        Next Step
                    </button>
                    <button v-else class="primary-btn" :disabled="enrolling" @click="handleEnroll">
                        <span v-if="enrolling">Loading...</span>
                        <span v-else>Enroll Now</span>
                    </button>
                </div>

                <v-alert v-if="showHelp" type="error" dense text class="medium-text mt-4" icon="mdi-lifebuoy" color="primary">
                    <span class="strong-text--text">
                        Contact <router-link :to="{name: 'help'}"><b class="primary--text text-decoration-underline">Support</b></router-link> for help.
                    </span>
                </v-alert>
            </div>
        </div>
    </div>
</template>

<script>
import WizardSidebar from './WizardSidebar.vue';
import WizardStepBasicInfo from './WizardStepBasicInfo.vue';
import WizardStepEvaluation from './WizardStepEvaluation.vue';
import WizardStepTrialClass from './WizardStepTrialClass.vue';
import WizardStepSchedule from './WizardStepSchedule.vue';
import WizardStepFinal from './WizardStepFinal.vue';

import GlobalComputed from '@/helpers/global-computed';

export default {
    name: 'EnrollmentWizard',
    components: {
        WizardSidebar,
        WizardStepBasicInfo,
        WizardStepEvaluation,
        WizardStepTrialClass,
        WizardStepSchedule,
        WizardStepFinal
    },
    props: {
        course: {
            type: Object,
            required: true
        },
        courseMeta: {
            type: Object,
            default: null
        },
        wizardMeta: {
            type: Object,
            default: null
        },
        mobile: {
            type: Boolean,
            default: false
        },
        enrolling: {
            type: Boolean,
            default: false
        }
    },
    data() {
        const d = new Date();
        d.setDate(d.getDate() + 1); // min start_date is tomorrow

        const trialMin = new Date();

        const max = new Date();
        max.setFullYear(max.getFullYear() - 3);

        return {
            currentStep: 1,
            showHelp: false,
            minStartDate: (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().substr(0, 10); })(),
            minTrialDate: new Date().toISOString().substr(0, 10),
            maxDateOfBirth: max.toISOString().substr(0, 10),
            formData: {
                dateOfBirth: '',
                preferredDays: [],
                evaluationBookedStart: '', // NEW — UTC datetime from calendar picker
                trialDate: '',
                trialTime: '',
                classDuration: '',
                classesPerWeek: '',
                startDate: '',
                comment: '',
                timeEntries: [
                    { day: '', from: '', to: '' },
                    { day: '', from: '', to: '' },
                    { day: '', from: '', to: '' },
                ]
            },
        };
    },
    computed: {
        /**
         * Dynamic steps based on wizard_meta from backend:
         * - Basic Info: only if user has no DOB
         * - Evaluation: only if user needs it (2nd+ enrollment, or old user without eval)
         * - Trial, Schedule, Final: always shown
         */
        activeSteps() {
            const steps = [];

            // Step: Basic Info — only if user hasn't entered DOB yet
            if (!this.userHasDob) {
                steps.push({ 
                    label: 'Basic Info', 
                    key: 'basic',
                    title: 'Your Basic Information',
                    subtitle: 'Let us know a few things to personalize your course experience'
                });
            }

            // Step: Evaluation Class — needed unless first enrollment after standalone eval
            if (this.needsEvaluationStep) {
                steps.push({ 
                    label: 'Evaluation Class', 
                    key: 'evaluation',
                    title: 'Book Your Evaluation Session',
                    subtitle: 'Select a date and time for your 15-minute evaluation session'
                });
            }

            // Always show:
            steps.push({ 
                label: 'Trial Class', 
                key: 'trial',
                title: 'Book Your Free Trial',
                subtitle: 'Choose a suitable date and time for your free trial session'
            });
            steps.push({ 
                label: 'Weekly Schedule', 
                key: 'schedule',
                title: 'Set Your Weekly Availability',
                subtitle: "Tell us how many classes you want and when you're available"
            });
            steps.push({ 
                label: 'Final Details', 
                key: 'final',
                title: 'Final Touches',
                subtitle: 'Add any notes or preferences before we confirm your enrollment'
            });

            return steps;
        },
        currentStepConfig() {
            return this.activeSteps[this.currentStep - 1] || {};
        },
        currentStepKey() {
            return this.currentStepConfig.key || '';
        },
        userHasDob() {
            if (this.wizardMeta) {
                return this.wizardMeta.user_has_dob;
            }
            // fallback: check user object
            return !!(this.user && this.user.date_of_birth);
        },
        needsEvaluationStep() {
            if (!this.wizardMeta) return true; // default: show eval step (safe default)
            
            const { user_enrollments_count, has_standalone_evaluation } = this.wizardMeta;
            
            // New user, first enrollment (0 existing), standalone eval done or pending → NO eval step
            if (user_enrollments_count === 0 && has_standalone_evaluation) {
                return false;
            }
            // Everyone else → eval step
            return true;
        },
        whatsappNumber() {
            return this.appData?.whatsapp_number || '';
        },
        courseDurationItems() {
            if (!this.courseMeta) {
                return [];
            }
            const items = this.courseMeta.class_duration_items;
            return items.map(item => {
                return {
                    text: item.text,
                    value: item.value,
                };
            });
        },
        classesPerWeekItems() {
            let items = [
                { text: '2 classes / week', value: 2 },
                { text: '3 classes / week', value: 3 },
                { text: '4 classes / week', value: 4 },
                { text: '5 classes / week', value: 5 },
                { text: '6 classes / week', value: 6 },
            ];
            // if one class duration not less than 1 hour, add one class to items
            if (this.formData.classDuration && this.formData.classDuration / 60 >= 1) {
                items.unshift({ text: '1 class / week', value: 1 });
            }
            return items;
        },
        enrollmentData() {
            const data = {
                class_duration: this.formData.classDuration,
                weekly_classes: this.formData.classesPerWeek,
                start_day: this.formData.startDate,
                suggested_trial_day: this.formData.trialDate,
                trial_time: this.formData.trialTime,
                comment: this.formData.comment,
                preferred_days: this.formData.preferredDays,
                schedule: this.formData.timeEntries.filter(item => !!item.day || item.day === 0),
            };

            // Only include date_of_birth if the user doesn't already have one
            if (!this.userHasDob && this.formData.dateOfBirth) {
                data.date_of_birth = this.formData.dateOfBirth;
            }

            // Only include evaluation_booked_start if evaluation step is active
            if (this.needsEvaluationStep && this.formData.evaluationBookedStart) {
                data.evaluation_booked_start = this.formData.evaluationBookedStart;
            }

            return data;
        },
        ...GlobalComputed
    },
    methods: {
        nextStep() {
            if (this.validateCurrentStep()) {
                if (this.currentStep < this.activeSteps.length) {
                    this.currentStep++;
                }
            }
        },
        previousStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
        },
        validateCurrentStep() {
            const key = this.currentStepKey;
            
            switch (key) {
                case 'basic':
                    if (!this.formData.dateOfBirth) {
                        this.$emit('show-message', { text: 'Date of Birth is required', type: 'error' });
                        return false;
                    }
                    return true;
                    
                case 'evaluation':
                    if (!this.formData.evaluationBookedStart) {
                        this.$emit('show-message', { text: 'Please select an evaluation time slot', type: 'error' });
                        return false;
                    }
                    return true;
                    
                case 'trial':
                    if (!this.formData.trialDate) {
                        this.$emit('show-message', { text: 'Trial Date is required', type: 'error' });
                        return false;
                    }
                    if (!this.formData.trialTime) {
                        this.$emit('show-message', { text: 'Trial Time is required', type: 'error' });
                        return false;
                    }
                    return true;
                    
                case 'schedule': {
                    if (!this.formData.startDate) {
                        this.$emit('show-message', { text: 'Start Date is required', type: 'error' });
                        return false;
                    }
                    if (!this.formData.classDuration) {
                        this.$emit('show-message', { text: 'Class Duration is required', type: 'error' });
                        return false;
                    }
                    if (!this.formData.classesPerWeek) {
                        this.$emit('show-message', { text: 'Classes per Week is required', type: 'error' });
                        return false;
                    }
                    if (!this.formData.preferredDays || this.formData.preferredDays.length === 0) {
                        this.$emit('show-message', { text: 'Please select at least one preferred day', type: 'error' });
                        return false;
                    }
                    if (this.formData.preferredDays.length < this.formData.classesPerWeek) {
                        this.$emit('show-message', { text: '"Preferred Days" must be equal or more than "Classes/ Week"', type: 'error' });
                        this.showHelp = true;
                        return false;
                    }
                    const scheduleEntries = this.formData.timeEntries.filter(item => !!item.day || item.day === 0);
                    if (scheduleEntries.length < this.formData.classesPerWeek) {
                        this.$emit('show-message', { text: '"Schedule" times must be equal or more than "Classes/ Week"', type: 'error' });
                        this.showHelp = true;
                        return false;
                    }
                    let timeValid = true;
                    scheduleEntries.forEach(scheduleItem => {
                        if (!scheduleItem.from) {
                            this.$emit('show-message', { text: 'Time input is required for all schedule entries', type: 'error' });
                            this.showHelp = true;
                            timeValid = false;
                        }
                    });
                    if (!timeValid) return false;
                    return true;
                }
                    
                case 'final':
                    return true;
                    
                default:
                    return true;
            }
        },
        /**
         * Called by Course.vue via $refs when the backend rejects the evaluation slot.
         * Jumps the wizard back to the evaluation step so the student can re-pick.
         */
        goToEvaluationStep() {
            const evalIndex = this.activeSteps.findIndex(s => s.key === 'evaluation');
            if (evalIndex !== -1) {
                this.currentStep = evalIndex + 1; // steps are 1-indexed
                this.formData.evaluationBookedStart = null; // reset selection
            }
        },
        handleEnroll() {
            // Validate all steps before submitting
            let allStepsValid = true;
            const savedStep = this.currentStep;
            
            for (let i = 1; i <= this.activeSteps.length; i++) {
                this.currentStep = i;
                if (!this.validateCurrentStep()) {
                    allStepsValid = false;
                    break;
                }
            }

            if (!allStepsValid) return;
            
            // Reset to last step
            this.currentStep = this.activeSteps.length;
            this.$emit('enroll', this.enrollmentData);
        }
    }
};
</script>

<style lang="scss" scoped>
.enrollment-wizard {
    background-color: #FAFAFA;
    border-radius: 8px;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
}

.wizard-container {
    display: flex;
    background: #FFFFFF;
    border-radius: 8px;
    box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.12);
    min-height: 500px;

    &.wizard-mobile {
        flex-direction: column;
    }
}

.wizard-content {
    flex: 1;
    padding: 48px;
    display: flex;
    flex-direction: column;
}

.content-header {
    margin-bottom: 40px;
}

.step-counter {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.21em;
    color: rgba(30, 30, 30, 0.48);
    display: block;
    margin-bottom: 20px;
}

.content-title {
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 24px;
    line-height: 1.21em;
    color: #1E1E1E;
    margin-bottom: 12px;
}

.content-subtitle {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.21em;
    color: rgba(30, 30, 30, 0.64);
    margin: 0;
}

.wizard-form {
    flex: 1;
}

.wizard-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 32px;
    gap: 16px;
}

.back-link {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: #1E1E1E;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.7;
    }
}

.primary-btn {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: #FFFFFF;
    background-color: #E79007;
    border: none;
    border-radius: 6px;
    padding: 10px 24px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: #d18306;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

// Ensure form inputs use Inter font
.wizard-form {
    ::v-deep .v-text-field,
    ::v-deep .v-select,
    ::v-deep .v-textarea {
        font-family: 'Inter', sans-serif;
    }

    ::v-deep .v-label {
        font-family: 'Inter', sans-serif;
    }

    ::v-deep .v-input__slot {
        font-family: 'Inter', sans-serif;
    }
}

@media (max-width: 960px) {
    .enrollment-wizard {
        border-radius: 12px;
    }

    .wizard-container {
        border-radius: 12px;
    }

    .wizard-content {
        padding: 20px 16px;
    }

    .content-header {
        margin-bottom: 24px;
    }

    .step-counter {
        font-size: 14px;
        margin-bottom: 12px;
    }

    .content-title {
        font-size: 18px;
        margin-bottom: 8px;
    }

    .content-subtitle {
        font-size: 14px;
    }

    .wizard-actions {
        flex-direction: column-reverse;
        gap: 12px;
        align-items: stretch;
        margin-top: 24px;
    }

    .primary-btn {
        width: 100%;
        font-size: 14px;
        padding: 12px 20px;
    }

    .back-link {
        font-size: 14px;
        text-align: center;
        padding: 8px 0;
    }
}
</style>
